import type { CSSProperties, JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'

import { useLayoutEffect, useRef, useState } from 'react'
import { TextBox } from 'atoms/TextBox'
import { SlidingTitleContentSC, SlidingTitleSC } from './SlidingTitle.styled'

export interface ISlidingTitleProps {
  title: string
  className?: string
  font?: ITextBoxProps['font']
  color?: ITextBoxProps['color']
}

const MIN_DURATION_S = 4
const EDGE_PAUSE_S = 1.2
const PIXELS_PER_SECOND = 40

export function SlidingTitle({
  title,
  className,
  font,
  color,
}: ISlidingTitleProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLSpanElement>(null)
  const [distance, setDistance] = useState(0)

  useLayoutEffect(() => {
    const container = containerRef.current
    const content = contentRef.current

    if (!container || !content) {
      return undefined
    }

    const measure = () => {
      const overflow = Math.ceil(content.scrollWidth - container.clientWidth)
      setDistance(overflow > 0 ? overflow : 0)
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(container)
    resizeObserver.observe(content)

    return () => resizeObserver.disconnect()
  }, [title, font])

  const isSliding = distance > 0
  const duration =
    MIN_DURATION_S + distance / PIXELS_PER_SECOND + EDGE_PAUSE_S * 2

  return (
    <SlidingTitleSC ref={containerRef} className={className}>
      <SlidingTitleContentSC
        ref={contentRef}
        $sliding={isSliding}
        style={
          isSliding
            ? ({
                '--sliding-title-distance': `${distance}px`,
                '--sliding-title-duration': `${duration}s`,
              } as CSSProperties)
            : undefined
        }
      >
        <TextBox title={title} font={font} color={color} maxLines={1} />
      </SlidingTitleContentSC>
    </SlidingTitleSC>
  )
}
